import { ArrowLeft, Maximize2, MessageCircle, Minimize2, Plus, Search, X } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { ConversationSettingsButton } from './conversation-settings-button'

import { Conversation } from '@/types/chat.type'

// Custom hook for mobile detection
const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = React.useState<boolean>(false)

  React.useEffect(() => {
    const checkIsMobile = (): void => {
      setIsMobile(window.innerWidth < 1024) // 640px is Tailwind's sm breakpoint
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)

    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  return isMobile
}

interface ChatHeaderProps {
  title: string
  conversationName?: string
  conversation?: Conversation
  hasOnlineParticipant?: boolean
  onSearch: () => void
  onCreateGroupChat?: () => void
  onClose: () => void
  onBack?: () => void
  onToggleMaximize?: () => void
  isMaximized?: boolean
  isPending: boolean
  isMobileConversationList: boolean
  compact?: boolean
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  title,
  conversationName,
  conversation,
  hasOnlineParticipant = false,
  onSearch,
  onCreateGroupChat,
  onClose,
  onBack,
  onToggleMaximize,
  isMaximized,
  isPending,
  isMobileConversationList,
  compact = false,
}) => {
  const isMobile = useIsMobile()

  const containerPadding = compact ? 'p-2' : 'p-3 sm:p-4'
  const titleClass = compact ? 'text-sm font-semibold text-gray-900' : 'text-base sm:text-lg font-semibold text-gray-900'
  const iconClass = compact ? 'h-3 w-3' : 'h-3 w-3 sm:h-4 sm:w-4'
  const leadIconClass = compact ? 'h-3.5 w-3.5 text-blue-600' : 'h-4 w-4 sm:h-5 sm:w-5 text-blue-600'
  const backIconClass = compact ? 'h-3.5 w-3.5' : 'h-4 w-4'
  const closeIconClass = compact ? 'h-3 w-3' : 'h-3 w-3 sm:h-4 sm:w-5'
  const btnPadding = compact ? 'p-1' : 'p-1 sm:p-2'

  return (
    <div className={`flex items-center justify-between ${containerPadding} border-b border-gray-200`}>
      <div className={`flex items-center ${compact ? 'gap-1' : 'gap-2 sm:gap-3'}`}>
        <div className={`flex items-center ${compact ? 'gap-1' : 'gap-1 sm:gap-2'}`}>
          {onBack && !isMobileConversationList ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className={`text-gray-600 hover:text-gray-700 ${btnPadding}`}
              title="Back to conversations"
            >
              <ArrowLeft className={backIconClass} />
            </Button>
          ) : (
            <MessageCircle className={leadIconClass} />
          )}
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 max-w-[220px]">
                  <h3 className={`${titleClass} truncate max-w-[180px]`}>
                    {isMobile ? conversationName || title : title}
                  </h3>
                  {hasOnlineParticipant && (
                    <span
                      className="h-2 w-2 flex-shrink-0 rounded-full bg-green-500"
                      title="Someone is online"
                      aria-label="Someone is online"
                    />
                  )}
                </div>
              </TooltipTrigger>
              {isMobile && conversationName && conversationName !== title && (
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-sm">{conversationName}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className={`flex items-center ${compact ? 'gap-0.5' : 'gap-1 sm:gap-2'}`}>
        {onCreateGroupChat && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCreateGroupChat}
            className={`text-gray-600 hover:text-blue-600 ${btnPadding}`}
            title="Create new group chat"
          >
            <Plus className={iconClass} />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onSearch}
          className={`text-gray-600 hover:text-gray-700 ${btnPadding}`}
          title="Search users and messages"
        >
          <Search className={iconClass} />
        </Button>
        {conversation && conversation.participants && <ConversationSettingsButton conversation={conversation} />}
        {onToggleMaximize && !isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleMaximize}
            className={`text-gray-600 hover:text-gray-700 ${btnPadding}`}
            title={isMaximized ? 'Minimize' : 'Maximize'}
          >
            {isMaximized ? <Minimize2 className={iconClass} /> : <Maximize2 className={iconClass} />}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending} className={btnPadding}>
          <X className={closeIconClass} />
        </Button>
      </div>
    </div>
  )
}
